import { useEffect, useState, type RefObject } from 'react';
import { getListScrollRoot } from '@features/proposals/ElementListRow/element-list-scroll.helpers';
import { isFirstRowFlushWithFileHeader } from './import-header-divider.helpers';

/**
 * Whether the first import row is still flush under the sticky file header.
 * Used so the header divider can take the selected colour only while that
 * row is adjacent — not after it has scrolled out of view.
 */
export const useFirstRowFlushWithFileHeader = (
  headerRef: RefObject<HTMLElement | null>,
  firstRowRef: RefObject<HTMLElement | null>,
  enabled: boolean,
): boolean => {
  const [flush, setFlush] = useState(true);

  useEffect(() => {
    if (!enabled) {
      setFlush(true);
      return;
    }

    const header = headerRef.current;
    const firstRow = firstRowRef.current;
    if (!header || !firstRow) return;

    const scrollRoot = getListScrollRoot(header);
    const update = (): void => {
      setFlush(
        isFirstRowFlushWithFileHeader(
          header.getBoundingClientRect().bottom,
          firstRow.getBoundingClientRect().top,
        ),
      );
    };

    update();
    scrollRoot?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    return () => {
      scrollRoot?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [enabled, headerRef, firstRowRef]);

  return enabled && flush;
};
